import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from "react-router-dom";
import { CaretDownIcon, ArrowUpRightIcon, ListIcon, XIcon } from '@phosphor-icons/react';
import { MAIN_MENU_ITEMS, CASE_STUDY_ROUTES } from '../config/menuConfig';
import { CONTACT_URL } from '../config/workConfig';
import useThrottledScroll from '../hooks/useThrottledScroll';

export default function Menu() {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useThrottledScroll(useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    setOpenDropdown(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path ? 'active' : '';
  const isWorkActive = () => CASE_STUDY_ROUTES.includes(location.pathname);

  const menuItems = MAIN_MENU_ITEMS;

  const toggleDropdown = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const renderMenuItem = (item) => {
    if (item.type === 'link') {
      return (
        <Link
          key={item.label}
          to={item.path}
          className={`menu-link ${isActive(item.path)}`}
        >
          {item.label}
        </Link>
      );
    }

    if (item.type === 'dropdown') {
      const isOpen = openDropdown === item.label;

      return (
        <div
          key={item.label}
          className="menu-dropdown"
          ref={dropdownRef}
        >
          <button
            className={`menu-link menu-dropdown-toggle ${isWorkActive() ? 'active' : ''}`}
            onClick={() => toggleDropdown(item.label)}
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-controls="work-dropdown-menu"
          >
            {item.label}
            <CaretDownIcon
              className={`menu-dropdown-arrow ${isOpen ? 'open' : ''}`}
            />
          </button>
          {isOpen && (
            <div id="work-dropdown-menu" role="menu" className="menu-dropdown-content">
              {item.items.map((childItem) => (
                <Link
                  key={childItem.path}
                  to={childItem.path}
                  role="menuitem"
                  className={`menu-dropdown-item ${isActive(childItem.path)}`}
                  onClick={() => setOpenDropdown(null)}
                >
                  {childItem.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'external') {
      return (
        <a
          key={item.label}
          href={item.href}
          className="menu-link menu-link--external"
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.label}
          <ArrowUpRightIcon className="menu-external-icon" aria-hidden />
        </a>
      );
    }

    return null;
  };

  return(
    <header className="menu-wrapper" role="banner">
      <nav className={`menu-bar ${scrolled ? 'scrolled' : ''}`}>
        <div className="menu-container">
          <button
            type="button"
            className="menu-mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <XIcon /> : <ListIcon />}
          </button>
          <div className={`menu-mobile-panel ${mobileOpen ? 'menu-mobile-panel--open' : ''}`}>
            <div className="menu-links">
              {menuItems.map(renderMenuItem)}
            </div>
            <a
              href={CONTACT_URL}
              className="menu-link menu-link--external"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
            >
              Get in touch
              <ArrowUpRightIcon className="menu-external-icon" aria-hidden />
            </a>
          </div>
          <div className="menu-desktop">
            <div className="menu-links">
              {menuItems.map(renderMenuItem)}
            </div>
            <a
              href={CONTACT_URL}
              className="menu-link menu-link--external"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get in touch
              <ArrowUpRightIcon className="menu-external-icon" aria-hidden />
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
}
